import { useEffect, useRef } from 'react';
import { useLocation } from 'wouter';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';

interface TelegramAuthData {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

interface TelegramLoginButtonProps {
  onAuth?: (user: TelegramAuthData) => void;
  mode?: 'login' | 'link';
  buttonSize?: 'large' | 'medium' | 'small';
}

declare global {
  interface Window {
    onTelegramAuth?: (user: TelegramAuthData) => void;
  }
}

export function TelegramLoginButton({
  onAuth,
  mode = 'login',
  buttonSize = 'large'
}: TelegramLoginButtonProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const { refetch } = useAuth();

  useEffect(() => {
    const botUsername = import.meta.env.VITE_TELEGRAM_BOT_USERNAME;

    if (!botUsername) {
      console.warn('Telegram bot username not configured');
      return;
    }

    // Define callback function
    window.onTelegramAuth = async (user: TelegramAuthData) => {
      try {
        const endpoint = mode === 'login' ? '/api/auth/telegram' : '/api/auth/telegram/link';

        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(user),
          credentials: 'include',
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || `Telegram ${mode} failed`);
        }

        const result = await response.json();

        if (onAuth) {
          onAuth(user);
        }

        // Refetch user data
        await refetch();

        toast({
          title: 'Success',
          description: mode === 'login'
            ? 'Successfully logged in with Telegram'
            : 'Successfully linked Telegram account',
        });

        if (mode === 'login') {
          setLocation('/');
        }
      } catch (error) {
        toast({
          title: 'Error',
          description: error instanceof Error ? error.message : `Telegram ${mode} failed`,
          variant: 'destructive',
        });
      }
    };

    // Load Telegram widget script
    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.async = true;
    script.setAttribute('data-telegram-login', botUsername);
    script.setAttribute('data-size', buttonSize);
    script.setAttribute('data-onauth', 'onTelegramAuth(user)');
    script.setAttribute('data-request-access', 'write');

    if (containerRef.current) {
      containerRef.current.appendChild(script);
    }

    return () => {
      // Cleanup
      if (containerRef.current && script.parentNode === containerRef.current) {
        containerRef.current.removeChild(script);
      }
      delete window.onTelegramAuth;
    };
  }, [mode, buttonSize, onAuth, toast, setLocation, refetch]);

  return <div ref={containerRef} className="telegram-login-button" />;
}
